import axios from 'axios';
import type {
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  User,
  UserPermissions,
  Ticket,
  TicketLane,
  Message,
  SendMessageRequest,
  UpdateTicketRequest,
  WhatsAppConnection,
  ChannelType,
  DaySchedule,
  QuickReply,
  CreateQuickReplyRequest,
  ScheduledMessage,
  CreateScheduledMessageRequest,
  UpdateScheduledMessageRequest,
  Tag,
  CreateTagRequest,
  Pipeline,
  CreatePipelineRequest,
  PipelineStage,
  CreateStageRequest,
  Deal,
  CreateDealRequest,
  UpdateDealRequest,
  DealNote,
  CompanySettings,
  UpdateCompanySettingsRequest,
  SubscriptionPlan,
  Subscription,
  Integration,
  CreateIntegrationRequest,
  SystemSettings,
  IntegrationSettings,
  MetaChannelsStatus,
  Contact,
  Flow,
  FlowExecution,
  FlowNode,
  FlowEdge,
  FlowAnalytics,
  FlowResultsResponse,
  AIAgent,
  CreateAgentRequest,
  UpdateAgentRequest,
  AgentExecution,
  KnowledgeBase,
  KnowledgeDocument,
  CreateKnowledgeBaseRequest,
  UpdateKnowledgeBaseRequest,
  CreateDocumentRequest,
  RAGQueryRequest,
  RAGQueryResponse,
  UserRole,
  Campaign,
  CampaignContact,
  CampaignLog,
  CampaignStats,
  CreateCampaignRequest,
  UpdateCampaignRequest,
  PersonalKanbanLane,
  PersonalKanbanItem,
  CreatePersonalKanbanLaneRequest,
  UpdatePersonalKanbanLaneRequest,
  AddPersonalKanbanItemRequest,
  MovePersonalKanbanItemRequest,
  Task,
  TaskComment,
  CreateTaskRequest,
  UpdateTaskRequest,
  CompleteTaskRequest,
  TaskStats,
  GroupNotificationSetting,
  GroupWithNotifications,
  BirthdaySetting,
  UpdateBirthdaySettingsRequest,
  BirthdayInfo,
  BirthdayLog,
  SendBirthdayMessageRequest,
  Queue,
  SLAStats,
  SLAInfo,
  TicketReopenReason,
  TicketReopenLog,
  CloseTicketWithReasonRequest,
  InternalChatGroup,
  InternalChatGroupMember,
  InternalChatMessage,
  InternalChatConversation,
  InternalChatUnreadCount,
  NPSResponse,
  NPSStats,
  NPSTimeline,
  NPSByUser,
  NPSByQueue,
  PinnedTicket,
  Lead,
  LeadStatus,
  LeadSource,
  LeadInteraction,
  LeadTask,
  LeadPipeline,
  MediaLibraryItem,
} from '@/types';

// Re-export Flow types for convenience
export type { Flow, FlowExecution, FlowNode, FlowEdge, FlowAnalytics, FlowResultsResponse };

// Use environment variable, or fallback to current origin (auto-detect domain)
// Detect placeholder strings from build and use fallback
const envApiUrl = process.env.REACT_APP_API_URL;
const API_BASE = (envApiUrl && !envApiUrl.startsWith('__'))
  ? envApiUrl
  : (typeof window !== 'undefined' ? window.location.origin : '');

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Necessário para enviar/receber cookies (OAuth)
});

const getMediaBaseUrl = (): string => {
  if (typeof window === 'undefined') return API_BASE || '';

  // In dev environment (port 3000), media files are served by backend on port 3333
  if (window.location.port === '3000') {
    return `${window.location.protocol}//${window.location.hostname}:3333`;
  }

  // In production or when API_BASE is explicitly set, use that
  if (API_BASE) return API_BASE;
  
  return window.location.origin;
};

// Helper function to get full media URL
export const getMediaUrl = (path: string | undefined | null): string => {
  if (!path) return '';
  const mediaBase = getMediaBaseUrl();
  // If already a full URL or blob URL, return as-is
  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('blob:')) {
    return path;
  }
  // Profile pictures use API endpoint with auto-refresh
  if (path.startsWith('/uploads/profile-pics/')) {
    const filename = path.replace('/uploads/profile-pics/', '');
    return `${mediaBase}/api/profile-pics/${filename}`;
  }
  // If starts with /, prepend base URL
  if (path.startsWith('/')) {
    return `${mediaBase}${path}`;
  }
  // Otherwise, assume it's a relative path
  return `${mediaBase}/${path}`;
};

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle response errors - specifically for 401 Unauthorized
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token is invalid or expired
      localStorage.removeItem('token');
      // Optionally redirect to login if needed
      // window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (data: LoginRequest) => api.post<AuthResponse>('/auth/login', data),
  register: (data: RegisterRequest) => api.post<AuthResponse>('/auth/register', data),
  me: () => api.get<User>('/auth/me'),
  forgotPassword: (email: string) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token: string, password: string) => api.post('/auth/reset-password', { token, password }),
  googleStart: () => api.get<{ url: string }>('/auth/google/start'),
};

// Pagination types
export interface PaginationInfo {
  page: number;
  limit: number;
  total?: number;
  totalPages?: number;
  hasMore: boolean;
}

export interface PaginatedResponse<T> {
  tickets: T[];
  pagination: PaginationInfo;
  statusCounts?: { open: number; pending: number; closed: number; groups: number };
}

export interface TicketListParams {
  status?: string;
  viewAll?: boolean;
  page?: number;
  limit?: number;
}

// Tickets
export const ticketsApi = {
  getAll: (params?: TicketListParams & { resolvedFilter?: 'mine' | 'queue' | 'all' }) =>
    api.get<PaginatedResponse<Ticket>>('/tickets', {
      params: {
        status: params?.status,
        viewAll: params?.viewAll ? 'true' : undefined,
        resolvedFilter: params?.resolvedFilter,
        page: params?.page || 1,
        limit: params?.limit || 50
      }
    }),
  getGroups: (params?: TicketListParams) =>
    api.get<PaginatedResponse<Ticket>>('/tickets/groups', {
      params: {
        status: params?.status,
        viewAll: params?.viewAll ? 'true' : undefined,
        page: params?.page || 1,
        limit: params?.limit || 50
      }
    }),
  getContacts: (params?: TicketListParams) =>
    api.get<PaginatedResponse<Ticket>>('/tickets/contacts', {
      params: {
        status: params?.status,
        viewAll: params?.viewAll ? 'true' : undefined,
        page: params?.page || 1,
        limit: params?.limit || 50
      }
    }),
  getById: (id: number) => api.get<Ticket>(`/tickets/${id}`),
  create: (data: { contactId: number; status?: string; whatsappConnectionId?: number }) => api.post<Ticket>('/tickets', data),
  update: (id: number, data: UpdateTicketRequest) => api.patch<Ticket>(`/tickets/${id}`, data),
  markAsRead: (id: number) => api.patch<Ticket>(`/tickets/${id}/read`, {}),
  delete: (id: number) => api.delete(`/tickets/${id}`),
  transfer: (id: number, data: { userId?: number; queueId?: number; whatsappConnectionId?: number; observation?: string }) =>
    api.post<Ticket>(`/tickets/${id}/transfer`, data),
  closeAll: (status?: string) => api.post('/tickets/close-all', {}, { params: { status } }),
  deleteAll: (status?: string) => api.delete('/tickets/delete-all', { params: { status } }),
  // Pinned tickets
  getPinned: () => api.get<{ pinnedTickets: PinnedTicket[]; count: number; maxAllowed: number }>('/tickets/pinned'),
  pin: (id: number) => api.post<{ success: boolean; pinnedTicket: PinnedTicket; message: string }>(`/tickets/${id}/pin`),
  unpin: (id: number) => api.delete<{ success: boolean; message: string }>(`/tickets/${id}/pin`),
  // Tags
  addTag: (ticketId: number, tagId: number) => api.post(`/tickets/${ticketId}/tags/${tagId}`),
  removeTag: (ticketId: number, tagId: number) => api.delete(`/tickets/${ticketId}/tags/${tagId}`),
};

// Ticket Lanes (Kanban)
export const ticketLanesApi = {
  getAll: () => api.get<TicketLane[]>('/ticket-lanes'),
  getById: (id: number) => api.get<TicketLane>(`/ticket-lanes/${id}`),
  create: (data: { name: string; color?: string; order?: number; isDefault?: boolean }) =>
    api.post<TicketLane>('/ticket-lanes', data),
  update: (id: number, data: { name?: string; color?: string; order?: number; isDefault?: boolean }) =>
    api.put<TicketLane>(`/ticket-lanes/${id}`, data),
  delete: (id: number) => api.delete(`/ticket-lanes/${id}`),
  reorder: (laneIds: number[]) => api.post('/ticket-lanes/reorder', { laneIds }),
  moveTicket: (ticketId: number, laneId: number | null) =>
    api.patch<Ticket>(`/ticket-lanes/ticket/${ticketId}/lane`, { laneId }),
};

// Users
export interface CreateUserRequest {
  name: string;
  email: string;
  password: string;
  role?: UserRole;
  signature?: string;
  signatureEnabled?: boolean;
  phone?: string;
  permissions?: UserPermissions;
}

export interface UpdateUserRequest {
  name?: string;
  email?: string;
  password?: string;
  role?: UserRole;
  active?: boolean;
  signature?: string;
  signatureEnabled?: boolean;
  profilePicUrl?: string;
  phone?: string;
  permissions?: UserPermissions;
}

export const usersApi = {
  getAll: () => api.get<User[]>('/users'),
  getById: (id: number) => api.get<User>(`/users/${id}`),
  create: (data: CreateUserRequest) => api.post<User>('/users', data),
  update: (id: number, data: UpdateUserRequest) => api.put<User>(`/users/${id}`, data),
  delete: (id: number) => api.delete(`/users/${id}`),
  resetPermissions: (id: number) => api.post<User>(`/users/${id}/reset-permissions`),
  getProfile: () => api.get<User>('/user/profile'),
  updateProfile: (data: {
    name?: string;
    signature?: string;
    signatureEnabled?: boolean;
    phone?: string;
    profilePicUrl?: string;
    menuExpanded?: boolean;
  }) => api.put<User>('/user/profile', data),
  updateMenuPreference: (menuExpanded: boolean) =>
    api.put<{ menuExpanded: boolean }>('/user/menu-preference', { menuExpanded }),
  uploadAvatar: (file: File) => {
    const formData = new FormData();
    formData.append('avatar', file);
    return api.post<{ profilePicUrl: string; message: string }>('/user/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  adminUploadAvatar: (userId: number, file: File) => {
    const formData = new FormData();
    formData.append('avatar', file);
    return api.post<{ profilePicUrl: string; message: string }>(`/users/${userId}/avatar`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  getQueues: (id: number) => api.get<any[]>(`/users/${id}/queues`),
};

// Messages
// Button types for interactive messages
export interface MessageButton {
  id: string;
  title: string;
}

export interface ListRow {
  id: string;
  title: string;
  description?: string;
}

export interface ListSection {
  title: string;
  rows: ListRow[];
}

export interface SendButtonsRequest {
  ticketId: number;
  body: string;
  footer?: string;
  buttons: MessageButton[];
}

export interface SendListRequest {
  ticketId: number;
  title: string;
  body: string;
  footer?: string;
  buttonText: string;
  sections: ListSection[];
}

export interface MessageListParams {
  cursor?: number;
  limit?: number;
}

export interface MessageListResponse {
  messages: Message[];
  pagination: {
    cursor: number;
    nextCursor: number;
    limit: number;
    hasMore: boolean;
  };
}

export interface LinkPreviewResponse {
  url: string;
  finalUrl: string;
  title: string;
  description: string;
  image: string;
  siteName: string;
  favicon: string;
}

export const messagesApi = {
  getByTicket: (ticketId: number, params?: MessageListParams) => {
    const queryParams = new URLSearchParams();
    if (params?.cursor) queryParams.set('cursor', params.cursor.toString());
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    const queryString = queryParams.toString();
    return api.get<MessageListResponse>(`/messages/ticket/${ticketId}${queryString ? `?${queryString}` : ''}`);
  },
  linkPreview: (url: string) => api.get<LinkPreviewResponse>('/messages/link-preview', { params: { url } }),
  send: (data: SendMessageRequest) => api.post<Message>('/messages/send', data),
  sendMedia: (formData: FormData) => api.post<Message>('/messages/send-media', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  sendButtons: (data: SendButtonsRequest) => api.post<Message>('/messages/send-buttons', data),
  sendButtonsWhaileys: (data: SendButtonsRequest) => api.post<Message>('/messages/send-buttons-whaileys', data),
  sendList: (data: SendListRequest) => api.post<Message>('/messages/send-list', data),
  delete: (id: number) => api.delete(`/messages/${id}`),
  edit: (id: number, newText: string) => api.put(`/messages/${id}`, { newText }),
  react: (id: number, emoji: string) => api.post(`/messages/${id}/react`, { emoji }),
  pin: (id: number) => api.post(`/messages/${id}/pin`),
  favorite: (id: number) => api.post(`/messages/${id}/favorite`),
  forward: (id: number, targetTicketId: number) => api.post(`/messages/${id}/forward`, { targetTicketId }),
};

// Connections
export interface ConnectionRequest {
  // Básicos
  name: string;
  channel?: ChannelType;
  isDefault?: boolean;
  provider?: string;

  // Grupos
  allowGroup?: boolean;
  groupAsTicket?: string;

  // Mensagens
  greetingMessage?: string;
  greetingMediaAttachment?: string;
  farewellMessage?: string;
  complationMessage?: string;
  outOfHoursMessage?: string;
  inactiveMessage?: string;
  collectiveVacationMessage?: string;
  collectiveVacationStart?: string;
  collectiveVacationEnd?: string;

  // Bot/Chatbot
  maxUseBotQueues?: number;
  timeUseBotQueues?: number;
  timeCreateNewTicket?: number;

  // Expiração de Tickets
  expiresTicket?: number;
  whenExpiresTicket?: string;
  expiresInactiveMessage?: string;
  timeInactiveMessage?: number;

  // NPS
  ratingMessage?: string;
  maxUseBotQueuesNPS?: number;
  expiresTicketNPS?: number;

  // Fila de Redirecionamento
  sendIdQueue?: number | null;
  timeSendQueue?: number;

  // Importação de Mensagens
  importOldMessages?: string | null;
  importRecentMessages?: string | null;
  closedTicketsPostImported?: boolean;
  importOldMessagesGroups?: boolean;
  queueIdImportMessages?: number | null;

  // Horários de Atendimento
  schedules?: DaySchedule[];

  // Flow Builder
  flowIdWelcome?: number | null;
  flowIdNotPhrase?: number | null;

  // Integrações
  integrationId?: number | null;
  promptId?: number | null;

  // Facebook/Instagram
  facebookUserId?: string;
  facebookUserToken?: string;
  facebookPageUserId?: string;
  tokenMeta?: string;

  // Filas
  queueIds?: number[];
}

export const connectionsApi = {
  getAll: () => api.get<WhatsAppConnection[]>('/connections'),
  create: (data: ConnectionRequest) => api.post<WhatsAppConnection>('/connections', data),
  update: (id: number, data: ConnectionRequest) => api.put<WhatsAppConnection>(`/connections/${id}`, data),
  connect: (id: number) => api.post<WhatsAppConnection>(`/connections/${id}/connect`),
  disconnect: (id: number) => api.post<WhatsAppConnection>(`/connections/${id}/disconnect`),
  refreshToken: (id: number) => api.post<{ token: string }>(`/connections/${id}/refresh-token`),
  requestNewQR: (id: number) => api.post(`/connections/${id}/new-qr`),
  getWhaileysQR: (id: number) => api.get<{ qrCode?: string; status?: string }>(`/connections/${id}/whaileys/qr`),
  pairPhone: (id: number, phoneNumber: string) =>
    api.post<{ success: boolean; pairingCode: string; message: string }>(
      `/connections/${id}/pair-phone`,
      { phoneNumber }
    ),
  delete: (id: number) => api.delete(`/connections/${id}`),
  // Message Sync
  getSyncs: (connectionId: number) => api.get<MessageSync[]>(`/connections/${connectionId}/sync`),
  startSync: (connectionId: number, data: StartSyncRequest) =>
    api.post<MessageSync>(`/connections/${connectionId}/sync`, data),
  getSync: (connectionId: number, syncId: number) =>
    api.get<MessageSync>(`/connections/${connectionId}/sync/${syncId}`),
  getSyncChats: (connectionId: number, syncId: number, page?: number, pageSize?: number) =>
    api.get<{ chats: MessageSyncChat[]; total: number; page: number; pageSize: number }>(
      `/connections/${connectionId}/sync/${syncId}/chats`,
      { params: { page, pageSize } }
    ),
  cancelSync: (connectionId: number, syncId: number) =>
    api.post(`/connections/${connectionId}/sync/${syncId}/cancel`),
  previewSync: (connectionId: number, startDate?: string, endDate?: string, includeGroups?: boolean) =>
    api.get<SyncPreview>(`/connections/${connectionId}/sync/preview`, { params: { startDate, endDate, includeGroups } }),
  getActiveSync: (connectionId: number) =>
    api.get<{ active: boolean; sync: MessageSync | null }>(`/connections/${connectionId}/sync/active`),
  forceDownload: (connectionId: number) =>
    api.post<{ message: string; status: string }>(`/connections/${connectionId}/sync/force-download`),
  getMessageCount: (connectionId: number, startDate?: string, endDate?: string) =>
    api.get<{ totalMessages: number; messagesWithTicket: number; orphanMessages: number }>(`/connections/${connectionId}/sync/message-count`, { params: { startDate, endDate } }),
};

// Message Sync Types
export interface MessageSync {
  id: number;
  companyId: number;
  connectionId: number;
  userId: number;
  startDate: string;
  endDate: string;
  createTickets: boolean;
  ticketStatus: string;
  includeGroups: boolean;
  tagId?: number;
  queueId?: number;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  totalChats: number;
  processedChats: number;
  totalMessages: number;
  processedMessages: number;
  createdContacts: number;
  createdTickets: number;
  skippedChats: number;
  errorCount: number;
  errorMessage?: string;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
  connection?: WhatsAppConnection;
  user?: User;
  tag?: Tag;
  queue?: Queue;
}

export interface MessageSyncChat {
  id: number;
  syncId: number;
  chatJid: string;
  phoneNumber: string;
  name: string;
  isGroup: boolean;
  messageCount: number;
  processed: boolean;
  skipped: boolean;
  skipReason?: string;
  errorMessage?: string;
  contactId?: number;
  ticketId?: number;
  createdAt: string;
  contact?: Contact;
  ticket?: Ticket;
}

export interface StartSyncRequest {
  startDate: string;
  endDate: string;
  syncFromWhatsApp?: boolean;
  createTickets: boolean;
  ticketStatus: string;
  includeGroups: boolean;
  skipEmptyChats: boolean;
  tagId?: number;
  queueId?: number;
}

export interface SyncPreview {
  totalContacts: number;
  totalGroups: number;
  newContacts: number;
  existingContacts: number;
  startDate: string;
  endDate: string;
}

// Quick Replies
export const quickRepliesApi = {
  getAll: () => api.get<QuickReply[]>('/quick-replies'),
  getById: (id: number) => api.get<QuickReply>(`/quick-replies/${id}`),
  getByShortcut: (shortcut: string) => api.get<QuickReply>(`/quick-replies/shortcut/${shortcut}`),
  create: (data: CreateQuickReplyRequest) => api.post<QuickReply>('/quick-replies', data),
  update: (id: number, data: Partial<CreateQuickReplyRequest>) => api.put<QuickReply>(`/quick-replies/${id}`, data),
  delete: (id: number) => api.delete(`/quick-replies/${id}`),
  uploadMedia: (formData: FormData) => api.post<{ mediaUrl: string; mimeType: string; mediaType: string; items?: Array<{ mediaUrl: string; mimeType: string; mediaType: string }> }>(
    '/quick-replies/upload',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  ),
  deleteMedia: (replyId: number, mediaId: number) => api.delete(`/quick-replies/${replyId}/media/${mediaId}`),
};

// Scheduled Messages
export const scheduledMessagesApi = {
  getAll: (status?: string) => api.get<ScheduledMessage[]>('/scheduled-messages', { params: { status } }),
  getById: (id: number) => api.get<ScheduledMessage>(`/scheduled-messages/${id}`),
  getByContact: (phone: string) => api.get<ScheduledMessage[]>('/scheduled-messages/by-contact', { params: { phone } }),
  create: (data: CreateScheduledMessageRequest) => api.post<ScheduledMessage>('/scheduled-messages', data),
  update: (id: number, data: UpdateScheduledMessageRequest) => api.put<ScheduledMessage>(`/scheduled-messages/${id}`, data),
  delete: (id: number) => api.delete(`/scheduled-messages/${id}`),
  cancel: (id: number) => api.post(`/scheduled-messages/${id}/cancel`),
};

// Campaigns (Mass Messaging)
export const campaignsApi = {
  getAll: (params?: { status?: string; connectionId?: number }) => api.get<Campaign[]>('/campaigns', { params }),
  getById: (id: number) => api.get<Campaign>(`/campaigns/${id}`),
  getStats: (id: number) => api.get<CampaignStats>(`/campaigns/${id}/stats`),
  getContacts: (id: number, params?: { status?: string; page?: number; limit?: number }) =>
    api.get<{ data: CampaignContact[]; total: number; page: number; limit: number }>(`/campaigns/${id}/contacts`, { params }),
  getLogs: (id: number) => api.get<CampaignLog[]>(`/campaigns/${id}/logs`),
  create: (data: CreateCampaignRequest) => api.post<Campaign>('/campaigns', data),
  update: (id: number, data: UpdateCampaignRequest) => api.put<Campaign>(`/campaigns/${id}`, data),
  delete: (id: number) => api.delete(`/campaigns/${id}`),
  addContacts: (id: number, data: { contactIds?: number[]; useFilters?: boolean }) =>
    api.post<{ success: boolean; added: number; total: number }>(`/campaigns/${id}/contacts`, data),
  removeContact: (id: number, contactId: number) => api.delete(`/campaigns/${id}/contacts/${contactId}`),
  start: (id: number) => api.post<Campaign>(`/campaigns/${id}/start`),
  pause: (id: number) => api.post<Campaign>(`/campaigns/${id}/pause`),
  cancel: (id: number) => api.post<Campaign>(`/campaigns/${id}/cancel`),
  duplicate: (id: number) => api.post<Campaign>(`/campaigns/${id}/duplicate`),

  // Import contacts from CSV file
  importCSV: (id: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<{
      success: boolean;
      imported: number;
      skipped: number;
      errors: number;
      total: number;
      messages: string[];
    }>(`/campaigns/${id}/import-csv`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Import contacts from text (one per line or comma/semicolon separated)
  importText: (id: number, contacts: string) =>
    api.post<{
      success: boolean;
      imported: number;
      skipped: number;
      total: number;
    }>(`/campaigns/${id}/import-text`, { contacts }),

  // Download CSV template
  downloadTemplate: () => api.get('/campaigns/csv-template', { responseType: 'blob' }),
};

// Tags
export const tagsApi = {
  getAll: () => api.get<Tag[]>('/tags'),
  getById: (id: number) => api.get<Tag>(`/tags/${id}`),
  getContacts: (tagId: number) => api.get<Contact[]>(`/tags/${tagId}/contacts`),
  getTickets: (tagId: number) => api.get<Ticket[]>(`/tags/${tagId}/tickets`),
  create: (data: CreateTagRequest) => api.post<Tag>('/tags', data),
  update: (id: number, data: Partial<CreateTagRequest>) => api.put<Tag>(`/tags/${id}`, data),
  delete: (id: number) => api.delete(`/tags/${id}`),
  addToContact: (tagId: number, contactId: number) => api.post(`/tags/${tagId}/contacts/${contactId}`),
  removeFromContact: (tagId: number, contactId: number) => api.delete(`/tags/${tagId}/contacts/${contactId}`),
  addToTicket: (tagId: number, ticketId: number) => api.post(`/tags/${tagId}/tickets/${ticketId}`),
  removeFromTicket: (tagId: number, ticketId: number) => api.delete(`/tags/${tagId}/tickets/${ticketId}`),
};

// Queues
export const queuesApi = {
  getAll: () => api.get('/queues'),
  getById: (id: number) => api.get(`/queues/${id}`),
  create: (data: { name: string; description?: string; color?: string; active?: boolean; connectionId?: number }) => api.post('/queues', data),
  update: (id: number, data: Partial<{ name: string; description?: string; color?: string; active?: boolean; connectionId?: number }>) => api.put(`/queues/${id}`, data),
  delete: (id: number) => api.delete(`/queues/${id}`),
  getUsers: (queueId: number) => api.get(`/queues/${queueId}/users`),
  assignUser: (queueId: number, userId: number) => api.post(`/queues/${queueId}/users`, { userId }),
  removeUser: (queueId: number, userId: number) => api.delete(`/queues/${queueId}/users/${userId}`),
  // SLA configuration
  getSLA: (queueId: number) => api.get<{ firstResponseSLA: number; resolutionSLA: number; slaEnabled: boolean }>(`/queues/${queueId}/sla`),
  updateSLA: (queueId: number, data: { firstResponseSLA: number; resolutionSLA: number; slaEnabled: boolean }) => api.put(`/queues/${queueId}/sla`, data),
};

// CRM - Pipelines
export const pipelinesApi = {
  getAll: () => api.get<Pipeline[]>('/crm/pipelines'),
  getById: (id: number) => api.get<Pipeline>(`/crm/pipelines/${id}`),
  create: (data: CreatePipelineRequest) => api.post<Pipeline>('/crm/pipelines', data),
  update: (id: number, data: Partial<CreatePipelineRequest>) => api.put<Pipeline>(`/crm/pipelines/${id}`, data),
  delete: (id: number) => api.delete(`/crm/pipelines/${id}`),
};

// CRM - Pipeline Stages
export const stagesApi = {
  getAll: (pipelineId: number) => api.get<PipelineStage[]>(`/crm/pipelines/${pipelineId}/stages`),
  create: (pipelineId: number, data: CreateStageRequest) => api.post<PipelineStage>(`/crm/pipelines/${pipelineId}/stages`, data),
  update: (pipelineId: number, stageId: number, data: Partial<CreateStageRequest>) => 
    api.put<PipelineStage>(`/crm/pipelines/${pipelineId}/stages/${stageId}`, data),
  delete: (pipelineId: number, stageId: number) => api.delete(`/crm/pipelines/${pipelineId}/stages/${stageId}`),
};

// CRM - Deals
export const dealsApi = {
  getAll: (params?: { status?: string; stageId?: number }) => api.get<Deal[]>('/crm/deals', { params }),
  getById: (id: number) => api.get<Deal>(`/crm/deals/${id}`),
  create: (data: CreateDealRequest) => api.post<Deal>('/crm/deals', data),
  update: (id: number, data: UpdateDealRequest) => api.put<Deal>(`/crm/deals/${id}`, data),
  delete: (id: number) => api.delete(`/crm/deals/${id}`),
  getNotes: (dealId: number) => api.get<DealNote[]>(`/crm/deals/${dealId}/notes`),
  addNote: (dealId: number, note: string) => api.post<DealNote>(`/crm/deals/${dealId}/notes`, { note }),
};

// Settings - Company
export const settingsApi = {
  getCompanySettings: () => api.get<CompanySettings>('/settings/company'),
  updateCompanySettings: (data: UpdateCompanySettingsRequest) => api.put<CompanySettings>('/settings/company', data),
  uploadCompanyLogo: (file: File) => {
    const formData = new FormData();
    formData.append('logo', file);
    return api.post<{ logoUrl: string; message: string }>('/settings/company/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getPlans: () => api.get<SubscriptionPlan[]>('/settings/plans'),
  getSubscription: () => api.get<Subscription>('/settings/subscription'),
  // White Label
  updateWhiteLabel: (data: { systemName: string; logoUrl?: string; backgroundUrl?: string }) => api.put('/settings/white-label', data),
  uploadWhiteLabelLogo: (formData: FormData) => api.post<{ logoUrl: string; message: string }>('/settings/white-label/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  uploadWhiteLabelBackground: (formData: FormData) => api.post<{ backgroundUrl: string; message: string }>('/settings/white-label/background', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  // Public branding (no authentication required)
  getPublicBranding: () => api.get<{ systemName: string; logoUrl: string; backgroundUrl: string }>('/public/branding'),
};

// Features API - accessible to ALL authenticated users (no settings permission needed)
export interface CompanyFeatures {
  hasTranscription: boolean;
}

export const featuresApi = {
  getCompanyFeatures: () => api.get<CompanyFeatures>('/settings/features'),
};

// Settings - Integrations
export const integrationsApi = {
  getAll: () => api.get<Integration[]>('/settings/integrations'),
  create: (data: CreateIntegrationRequest) => api.post<Integration>('/settings/integrations', data),
  update: (id: number, data: Partial<CreateIntegrationRequest>) => api.put<Integration>(`/settings/integrations/${id}`, data),
  delete: (id: number) => api.delete(`/settings/integrations/${id}`),
};

// Settings - System (Admin only)
export const systemSettingsApi = {
  get: () => api.get<SystemSettings>('/settings/system'),
  update: (data: Partial<SystemSettings>) => api.put<SystemSettings>('/settings/system', data),
};

// Settings - Integration Settings (FlowBuilder)
export interface AIHealthCheckResult {
  providers: {
    [key: string]: {
      success: boolean;
      message: string;
      provider: string;
      status?: string;
    };
  };
  totalProviders: number;
  onlineCount: number;
  timestamp: number;
}

export const integrationSettingsApi = {
  get: () => api.get<IntegrationSettings>('/settings/integration-settings'),
  update: (data: Partial<IntegrationSettings>) => api.put<IntegrationSettings>('/settings/integration-settings', data),
  test: (type: string) => api.post<{ success: boolean; message: string }>(`/settings/integration-settings/test/${type}`),
  aiHealthCheck: () => api.get<AIHealthCheckResult>('/settings/integration-settings/ai-health-check'),
};

// Meta Channels API (WhatsApp Cloud, Facebook, Instagram)
export const metaChannelsApi = {
  getStatus: () => api.get<MetaChannelsStatus>('/meta-channels/status'),
  testConnection: (channel: 'whatsapp' | 'facebook' | 'instagram') =>
    api.post<{ success: boolean; message: string; channel: string }>(`/meta-channels/test/${channel}`),
  sendMessage: (data: {
    channel: 'whatsapp' | 'facebook' | 'instagram';
    to: string;
    type: 'text' | 'image' | 'video' | 'audio' | 'document' | 'template';
    text?: string;
    mediaUrl?: string;
    caption?: string;
    ticketId?: number;
    replyTo?: string;
    templateName?: string;
    languageCode?: string;
    components?: any[];
  }) => api.post<{ success: boolean; messageId?: string }>('/meta-channels/send', data),

  // Meta Discovery - Auto-fetch Facebook pages and Instagram accounts
  discover: (accessToken: string) =>
    api.post<{
      facebookPages: Array<{
        id: string;
        name: string;
        accessToken: string;
        category?: string;
        picture?: { data: { url: string } };
        instagram_business_account?: {
          id: string;
          username?: string;
          name?: string;
          profile_picture_url?: string;
          followers_count?: number;
          media_count?: number;
        };
      }>;
      instagramAccounts: Array<{
        id: string;
        username?: string;
        name?: string;
        profile_picture_url?: string;
        followers_count?: number;
        media_count?: number;
      }>;
    }>('/meta-channels/discover', { accessToken }),

  selectFacebookPage: (data: { pageId: string; pageName: string; accessToken: string }) =>
    api.post<{ success: boolean; message: string; page: { id: string; name: string } }>(
      '/meta-channels/select-facebook-page',
      data
    ),

  selectInstagramAccount: (data: {
    accountId: string;
    username: string;
    accessToken: string;
    pageId: string;
  }) =>
    api.post<{ success: boolean; message: string; account: { id: string; username: string } }>(
      '/meta-channels/select-instagram-account',
      data
    ),

  exchangeToken: (shortLivedToken: string) =>
    api.post<{
      success: boolean;
      accessToken: string;
      expiresIn: number;
      expiresInDays: number;
    }>('/meta-channels/exchange-token', { shortLivedToken }),

  // WhatsApp Templates
  getTemplates: () =>
    api.get<{
      templates: Array<{
        id: string;
        name: string;
        language: string;
        status: string;
        category: string;
        components: Array<{
          type: string;
          format?: string;
          text?: string;
          buttons?: Array<{ type: string; text: string; url?: string }>;
        }>;
      }>;
      count: number;
    }>('/meta-channels/templates'),

  getTemplatesByConnection: (connectionId: number) =>
    api.get<{
      templates: Array<{
        id: string;
        name: string;
        language: string;
        status: string;
        category: string;
        components: Array<{
          type: string;
          format?: string;
          text?: string;
          buttons?: Array<{ type: string; text: string; url?: string }>;
        }>;
      }>;
      count: number;
    }>(`/meta-channels/templates/${connectionId}`),

  sendTemplate: (data: {
    to: string;
    templateName: string;
    languageCode?: string;
    components?: any[];
    ticketId?: number;
    connectionId?: number;
    templateData?: {
      header?: string;
      headerType?: string;
      body: string;
      footer?: string;
      buttons?: Array<{ type: string; text: string }>;
    };
  }) =>
    api.post<{ success: boolean; messageId: string }>('/meta-channels/send-template', data),

  getProfile: () =>
    api.get<{
      id: string;
      verified_name: string;
      display_phone_number: string;
      quality_rating: string;
    }>('/meta-channels/profile'),

  // Webhook Token Management
  generateWebhookToken: () =>
    api.post<{
      success: boolean;
      webhookToken: string;
      message: string;
      instructions: {
        step1: string;
        step2: string;
        step3: string;
        step4: string;
        step5: string;
      };
    }>('/meta-channels/generate-webhook-token'),

  getWebhookToken: () =>
    api.get<{
      hasToken: boolean;
      webhookToken: string;
      webhookUrl?: string;
      message?: string;
    }>('/meta-channels/webhook-token'),

  // Template Management CRUD
  getAllTemplates: (connectionId: number) =>
    api.get<{
      templates: Array<{
        id: string;
        name: string;
        language: string;
        status: string;
        category: string;
        components: Array<{
          type: string;
          format?: string;
          text?: string;
          example?: {
            header_text?: string[];
            body_text?: string[][];
          };
          buttons?: Array<{ type: string; text: string; url?: string }>;
        }>;
      }>;
      count: number;
    }>(`/meta-channels/templates/all/${connectionId}`),

  createTemplate: (connectionId: number, data: {
    name: string;
    language: string;
    category: string;
    components: Array<{
      type: string;
      format?: string;
      text?: string;
      example?: {
        header_text?: string[];
        body_text?: string[][];
        header_handle?: string[];
      };
      buttons?: Array<{
        type: string;
        text: string;
        url?: string;
        phone_number?: string;
        example?: string;
      }>;
    }>;
  }) =>
    api.post<{
      success: boolean;
      id: string;
      status: string;
      category: string;
      message: string;
    }>(`/meta-channels/templates/${connectionId}`, data),

  updateTemplate: (connectionId: number, templateId: string, data: {
    components: Array<{
      type: string;
      format?: string;
      text?: string;
      example?: {
        header_text?: string[];
        body_text?: string[][];
      };
      buttons?: Array<{
        type: string;
        text: string;
        url?: string;
      }>;
    }>;
  }) =>
    api.put<{ success: boolean; message: string }>(`/meta-channels/templates/${connectionId}/${templateId}`, data),

  deleteTemplate: (connectionId: number, templateName: string) =>
    api.delete<{ success: boolean; message: string }>(`/meta-channels/templates/${connectionId}/${templateName}`),
};

// Contacts
export const contactsApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string; tagIds?: string }) =>
    api.get<Contact[] | { data: Contact[]; total: number; page: number; limit: number }>('/contacts', { params }),
  getById: (id: number) => api.get<Contact>(`/contacts/${id}`),
  update: (id: number, data: Partial<Contact>) => api.put<Contact>(`/contacts/${id}`, data),
  delete: (id: number) => api.delete(`/contacts/${id}`),
  create: (data: Partial<Contact>) => api.post<Contact>('/contacts', data),
  refresh: (contactId: number) => api.post(`/contacts/${contactId}/refresh`),
  sync: (connectionId: number) => api.post<{ success: boolean; synced: number; failed: number; total: number }>('/contacts/sync', { connectionId }),
  verify: (connectionId: number, phones: string[]) => api.post<any[]>('/contacts/verify', { connectionId, phones }),
  getUserInfo: (connectionId: number, jids: string[]) => api.post<any>('/contacts/info', { connectionId, jids }),
  block: (jid: string, connectionId: number) => api.post(`/contacts/${jid}/block`, { connectionId, jid }),
  unblock: (jid: string, connectionId: number) => api.delete(`/contacts/${jid}/block`, { data: { connectionId, jid } }),
  getBlocklist: (connectionId: number) => api.get<string[]>('/contacts/blocklist', { params: { connectionId } }),
};

// Flow Builder API
export const flowsApi = {
  getAll: (status?: string) => api.get<Flow[]>('/flows', { params: { status } }),
  getById: (id: number) => api.get<Flow>(`/flows/${id}`),
  create: (data: Partial<Flow>) => api.post<Flow>('/flows', data),
  update: (id: number, data: Partial<Flow>) => api.put<Flow>(`/flows/${id}`, data),
  delete: (id: number) => api.delete(`/flows/${id}`),
  duplicate: (id: number) => api.post<Flow>(`/flows/${id}/duplicate`),
  getExecutions: (id: number, status?: string) => 
    api.get<FlowExecution[]>(`/flows/${id}/executions`, { params: { status } }),
  getAnalytics: (id: number) => 
    api.get<FlowAnalytics>(`/flows/${id}/analytics`),
  getResults: (id: number, page?: number, perPage?: number) => 
    api.get<FlowResultsResponse>(`/flows/${id}/results`, { params: { page, perPage } }),
  exportResults: (id: number, format?: 'json' | 'csv') =>
    api.get(`/flows/${id}/results/export`, { params: { format }, responseType: format === 'csv' ? 'blob' : 'json' }),
  execute: (id: number, contactId: number, ticketId?: number) => 
    api.post<FlowExecution>(`/flows/${id}/execute`, { contactId, ticketId }),
  uploadMedia: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<{ mediaUrl: string; mimeType: string; mediaType: string; fileName: string; fileSize: number }>(
      '/flows/upload-media',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },
  importN8n: (n8nWorkflow: any) =>
    api.post<Flow>('/flows/import/n8n', n8nWorkflow),
  validateN8n: (n8nWorkflow: any) =>
    api.post<{ valid: boolean; message?: string }>('/flows/import/validate', n8nWorkflow),
  generateWithAI: (description: string, provider?: string, model?: string) =>
    api.post<{ nodes: any[]; edges: any[] }>('/flows/generate-ai', { description, provider, model }),
};

// AI Agent
export interface AIProvider {
  id: string;
  name: string;
  description: string;
  available: boolean;
}

export interface AISuggestionResponse {
  suggestion: string;
  provider: string;
}

export interface AITicketAssistResponse {
  provider: string;
  model: string;
  correctedText: string;
  suggestions: string[];
}

export const aiApi = {
  getProviders: () => api.get<AIProvider[]>('/ai/providers'),
  chat: (provider: string, message: string, context?: string, contactName?: string) =>
    api.post<{ response: string; provider: string }>('/ai/chat', {
      provider, message, context, contactName
    }),
  suggest: (ticketId: number, provider?: string, lastCount?: number) =>
    api.post<AISuggestionResponse>('/ai/suggest', {
      ticketId, provider, lastCount
    }),
  ticketAssist: (data: { ticketId: number; provider?: string; model?: string; draftText?: string; lastCount?: number; includeAudio?: boolean; suggestionCount?: number }) =>
    api.post<AITicketAssistResponse>('/ai/tickets/assist', data),
  ticketChat: (data: { ticketId: number; message: string; provider?: string; model?: string; lastCount?: number; includeAudio?: boolean }) =>
    api.post<{ response: string; provider: string; model: string }>('/ai/tickets/chat', data),
  agentChat: (agentId: number, data: { mensagem: string; contexto?: string; ticketId?: number; contactId?: number }) =>
    api.post<{ resposta: string; conversationId?: number }>(`/ai/agent/${agentId}/chat`, data),
  agentGetConversation: (agentId: number, params?: { ticketId?: number; userId?: number; limit?: number }) =>
    api.get<{ conversation: any; messages: Array<{ role: string; content: string }> }>(`/ai/agent/${agentId}/conversation`, { params }),
  testApiKey: (provider: string, apiKey?: string) =>
    api.post<{ success: boolean; message: string }>(`/ai/test/${provider}`, { apiKey }),
};

// AI Agents API
export const agentsApi = {
  getAll: (status?: string) => api.get<{ agents: AIAgent[] }>('/ai-agents', { params: { status } }),
  getById: (id: number) => api.get<AIAgent>(`/ai-agents/${id}`),
  create: (data: CreateAgentRequest) => api.post<AIAgent>('/ai-agents', data),
  update: (id: number, data: UpdateAgentRequest) => api.put<AIAgent>(`/ai-agents/${id}`, data),
  delete: (id: number) => api.delete(`/ai-agents/${id}`),
  duplicate: (id: number) => api.post<AIAgent>(`/ai-agents/${id}/duplicate`),
  uploadAsset: (agentId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<{ mediaUrl: string; mimeType: string; mediaType: string; fileName: string; fileSize: number }>(
      `/ai-agents/${agentId}/assets/upload`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },
  uploadAssetImage: (agentId: number, file: File) => agentsApi.uploadAsset(agentId, file),
  getExecutions: (id: number, params?: { limit?: number; offset?: number }) =>
    api.get<AgentExecution[]>(`/ai-agents/${id}/executions`, { params }),
  chat: (id: number, message: string, options?: { conversationId?: string; contactId?: number; contactName?: string; ticketId?: number }) =>
    api.post<{ response: string; conversationId: number; toolCalls?: any[]; sources?: any[]; behavior?: any; memories?: any[] }>(`/ai-agents/${id}/chat`, {
      message,
      conversationId: options?.conversationId,
      contactId: options?.contactId,
      contactName: options?.contactName,
      ticketId: options?.ticketId,
    }),
  testAgent: (id: number, testMessage: string, contactId?: number, contactName?: string) =>
    api.post<{ response: string; tokensUsed: number; durationMs: number }>(`/ai-agents/${id}/chat`, {
      message: testMessage,
      contactId,
      contactName,
    }),

  // Knowledge Management
  knowledge: {
    // Categories
    getCategories: (agentId: number) =>
      api.get<{ categories: any[] }>(`/ai-agents/${agentId}/knowledge/categories`),
    getCategory: (agentId: number, categoryId: number) =>
      api.get<{ category: any; items: any[] }>(`/ai-agents/${agentId}/knowledge/categories/${categoryId}`),
    createCategory: (agentId: number, data: { name: string; type: string; description?: string; priority?: number }) =>
      api.post(`/ai-agents/${agentId}/knowledge/categories`, data),
    updateCategory: (agentId: number, categoryId: number, data: { name?: string; type?: string; description?: string; priority?: number; status?: string }) =>
      api.put(`/ai-agents/${agentId}/knowledge/categories/${categoryId}`, data),
    deleteCategory: (agentId: number, categoryId: number) =>
      api.delete(`/ai-agents/${agentId}/knowledge/categories/${categoryId}`),

    // Items
    getItems: (agentId: number, categoryId: number) =>
      api.get<{ items: any[] }>(`/ai-agents/${agentId}/knowledge/categories/${categoryId}/items`),
    createItem: (agentId: number, categoryId: number, data: { question?: string; answer: string; keywords?: string[]; priority?: number }) =>
      api.post(`/ai-agents/${agentId}/knowledge/categories/${categoryId}/items`, data),
    updateItem: (agentId: number, itemId: number, data: { question?: string; answer?: string; keywords?: string[]; priority?: number; status?: string }) =>
      api.put(`/ai-agents/${agentId}/knowledge/items/${itemId}`, data),
    deleteItem: (agentId: number, itemId: number) =>
      api.delete(`/ai-agents/${agentId}/knowledge/items/${itemId}`),

    // Import/Export
    import: (agentId: number, data: { categoryId?: number; items: { question?: string; answer: string; keywords?: string[] }[] }) =>
      api.post<{ imported: number }>(`/ai-agents/${agentId}/knowledge/import`, data),
    search: (agentId: number, query: string) =>
      api.get<{ items: any[] }>(`/ai-agents/${agentId}/knowledge/search`, { params: { q: query } }),
  },
};

// Knowledge Base API
export const knowledgeBaseApi = {
  // Knowledge Bases
  getAll: () => api.get<{ knowledgeBases: KnowledgeBase[] }>('/knowledge-bases'),
  getById: (id: number) => api.get<KnowledgeBase>(`/knowledge-bases/${id}`),
  create: (data: CreateKnowledgeBaseRequest) => api.post<KnowledgeBase>('/knowledge-bases', data),
  update: (id: number, data: UpdateKnowledgeBaseRequest) =>
    api.put<KnowledgeBase>(`/knowledge-bases/${id}`, data),
  delete: (id: number) => api.delete(`/knowledge-bases/${id}`),
  setDefault: (id: number) => api.post(`/knowledge-bases/${id}/set-default`),

  // Documents
  getDocuments: (kbId: number) => api.get<KnowledgeDocument[]>(`/knowledge-bases/${kbId}/documents`),
  getDocument: (kbId: number, docId: number) =>
    api.get<KnowledgeDocument>(`/knowledge-bases/${kbId}/documents/${docId}`),
  addDocument: (kbId: number, data: CreateDocumentRequest) =>
    api.post<KnowledgeDocument>(`/knowledge-bases/${kbId}/documents`, data),
  uploadDocument: (kbId: number, formData: FormData) =>
    api.post<KnowledgeDocument>(`/knowledge-bases/${kbId}/documents/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  deleteDocument: (kbId: number, docId: number) =>
    api.delete(`/knowledge-bases/${kbId}/documents/${docId}`),
  reprocessDocument: (kbId: number, docId: number) =>
    api.post(`/knowledge-bases/${kbId}/documents/${docId}/reprocess`),

  // RAG Query
  query: (data: RAGQueryRequest) => api.post<RAGQueryResponse>('/knowledge-bases/query', data),
  search: (knowledgeBaseId: number, query: string, topK?: number) =>
    api.post<RAGQueryResponse>(`/knowledge-bases/${knowledgeBaseId}/search`, { query, topK }),
};

// Transcription API
export const transcriptionApi = {
  transcribe: (messageId: number) =>
    api.post<{ text: string; success: boolean }>('/transcription/transcribe', { messageId }),
};

// Dashboard Statistics (Company Dashboard)
export interface CompanyDashboardStats {
  // Applied Filters
  filters?: {
    period: string;
    status: string;
    channel: string;
    agent: number;
    queue: number;
    connection: number;
    startDate: string;
    endDate: string;
  };

  // Tickets - All time
  totalTickets: number;
  pendingTickets: number;
  openTickets: number;
  closedTickets: number;
  ticketsBacklog?: number;

  // Tickets - Period specific
  ticketsInPeriod?: number;
  ticketsClosedInPeriod?: number;
  ticketsToday: number;
  ticketsClosedToday?: number;
  ticketsGrowth: number;
  ticketsTrend: number[];

  // Messages - All time
  messagesTotal: number;
  messagesSentTotal?: number;
  messagesReceivedTotal?: number;

  // Messages - Period specific
  messagesInPeriod?: number;
  messagesSentInPeriod?: number;
  messagesReceivedInPeriod?: number;
  messagesToday: number;
  messagesSentToday?: number;
  messagesReceivedToday?: number;
  messagesTrend: number[];

  // Message delivery health
  failedMessagesInPeriod?: number;
  pendingMessagesInPeriod?: number;
  failedMessagesToday?: number;
  pendingMessagesToday?: number;
  failedMessagesTrend?: number[];
  pendingMessagesTrend?: number[];

  // Contacts
  totalContacts: number;
  contactsToday?: number;
  contactsInPeriod?: number;
  contactsGrowth?: number;
  topContacts?: Array<{
    contactId: number;
    name: string;
    phoneNumber: string;
    messageCount: number;
    ticketCount: number;
  }>;

  // Connections
  totalConnections: number;
  activeConnections: number;

  // Scheduled Messages
  scheduledMessages: number;

  // Quick Replies
  quickRepliesTotal: number;
  quickRepliesUsage: number;

  // Tags
  totalTags: number;

  // Users
  activeUsers: number;

  // CRM
  totalDeals: number;
  dealsWon: number;
  dealsLost: number;
  dealsValue: number;
  conversionRate: number;
  dealsTrend: number[];

  // Performance metrics
  avgResponseTime: number;
  avgResolutionTime: number;
  satisfactionRate?: number;

  // Resolution & SLA
  resolutionRate?: number;
  slaComplianceRate?: number;
  ticketsWithinSLA?: number;
  ticketsWithFirstResponse?: number;

  // Time Distribution
  ticketsByDayOfWeek?: number[];
  busiestHours?: Array<{ hour: number; count: number }>;
  responseTimeDistribution?: Array<{ label: string; count: number }>;

  // Messages Analysis
  messagesByType?: Array<{ type: string; count: number }>;
  avgMessagesPerTicket?: number;

  // Campaigns
  totalCampaigns?: number;
  activeCampaigns?: number;
  completedCampaigns?: number;
  campaignMessagesSent?: number;
  campaignMessagesDelivered?: number;

  // Attention Required
  unansweredTickets?: number;

  // Additional admin metrics
  ticketsByHour?: number[];
  ticketsByQueue?: Array<{ queueId: number | null; count: number }>;
  topAgents?: Array<{ userId: number | null; name: string; count: number }>;
  ticketsAging?: Array<{ label: string; count: number }>;

  // NPS Metrics
  npsScore?: number;
  npsPromoters?: number;
  npsDetractors?: number;
  npsPassives?: number;
  npsTotalResponses?: number;
  npsAvgRating?: number;

  // Efficiency Metrics
  fcrRate?: number;
  reopenRate?: number;
  transferRate?: number;
  abandonmentRate?: number;
  responseRate?: number;
  ticketsPerAgent?: number;
  teamFCR?: number;

  // Agent Rankings
  topAgentsByTickets?: Array<{ userId: number; name: string; value: number }>;
  topAgentsByFCR?: Array<{ userId: number; name: string; value: number }>;
  topAgentsByRating?: Array<{ userId: number; name: string; value: number }>;
  topAgentsBySpeed?: Array<{ userId: number; name: string; value: number }>;

  // Chart Data
  ticketFlowData?: Array<{ date: string; created: number; closed: number }>;
  peakHoursData?: Array<{ hour: string; count: number }>;
  queueDistribution?: Array<{ queueId: number | null; queueName: string; count: number }>;
  dailyTrendData?: Array<{ date: string; volume: number; resolution: number }>;

  // Agent Performance Table
  agentPerformance?: Array<{
    userId: number;
    name: string;
    isOnline: boolean;
    tickets: number;
    fcr: number;
    firstResponseAvg: number;
    resolutionAvg: number;
    reopenRate: number;
    transferRate: number;
    abandonmentRate: number;
    msgsPerTicket: number;
    messagesSent: number;
    messagesReceived: number;
    evaluations: number;
    avgRating: number;
  }>;
  teamAverageMetrics?: {
    tickets: number;
    fcr: number;
    firstResponseAvg: number;
    resolutionAvg: number;
    reopenRate: number;
    transferRate: number;
    abandonmentRate: number;
    msgsPerTicket: number;
  };
}

// Admin Dashboard Statistics (Super Admin only)
export interface DashboardStats {
  totalCompanies: number;
  activeCompanies: number;
  totalUsers: number;
  totalSubscriptions: number;
  activeSubscriptions: number;
  totalRevenue: number;
  pendingInvoices: number;
  overdueInvoices: number;
}

export interface Company {
  id: number;
  name: string;
  email: string;
  active: boolean;
  createdAt: string;
  userCount: number;
  subscriptionPlan: string;
  subscriptionStatus: string;
  plan?: string;
  maxConnections?: number;
}

export interface CompanyDetails {
  company: Company;
  users: User[];
  subscription: Subscription;
  connections: WhatsAppConnection[];
}

export interface AdminSubscription {
  id: number;
  companyId: number;
  planId: number;
  status: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  company?: { name: string; email: string };
  subscriptionPlan?: { name: string; price: number };
}

export interface Invoice {
  id: number;
  companyId: number;
  invoiceNumber: string;
  status: string;
  total: number;
  subtotal: number;
  tax: number;
  discount: number;
  dueDate: string;
  paidAt?: string;
  company?: { name: string };
  items?: InvoiceItem[];
}

export interface InvoiceItem {
  id?: number;
  description: string;
  quantity: number;
  unitPrice: number;
  amount?: number;
}

export interface AuditLog {
  id: number;
  userId?: number;
  companyId?: number;
  action: string;
  entity: string;
  entityId: number;
  ipAddress: string;
  createdAt: string;
  user?: { name: string; email: string };
  company?: { name: string };
}

export interface AdminSystemSettings {
  id: number;
  allowSignup: boolean;
  maintenanceMode: boolean;
  companyName: string;
  supportEmail: string;
  supportPhone: string;
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  smtpFrom: string;
}

// Dashboard API
export const dashboardApi = {
  getStats: () => api.get<CompanyDashboardStats>('/dashboard/stats'),
};

// Billing / Mercado Pago API
export interface MercadoPagoSubscription {
  id: number;
  companyId: number;
  planId: number;
  mpSubscriptionId: string;
  payerEmail: string;
  payerName?: string;
  status: string;
  reason: string;
  transactionAmount: number;
  currencyId: string;
  nextPaymentDate?: string;
  lastPaymentDate?: string;
  startDate: string;
  endDate?: string;
  frequencyType: string;
  frequency: number;
  paymentCount: number;
  failedPaymentCount: number;
  createdAt: string;
  updatedAt: string;
  plan?: SubscriptionPlan;
}

export interface CreateSubscriptionRequest {
  planId: number;
  payerEmail: string;
  payerName?: string;
  payerDocType?: string;
  payerDocNumber?: string;
  cardToken?: string;
  backUrl?: string;
}

export interface SubscriptionResult {
  success: boolean;
  subscription?: MercadoPagoSubscription;
  initPoint?: string;
  message?: string;
  error?: string;
}

// Checkout request (PIX, boleto, cartão)
export interface CreateCheckoutRequest {
  planId: number;
  payerEmail: string;
  payerName?: string;
  payerDocType?: string;
  payerDocNumber?: string;
  paymentMethod?: 'pix' | 'boleto' | 'credit_card' | 'debit_card' | 'all';
  backUrl?: string;
  couponCode?: string;
}

export interface CheckoutResult {
  success: boolean;
  preferenceId?: string;
  initPoint?: string;
  message?: string;
  error?: string;
}

export interface MercadoPagoPayment {
  id: number;
  subscriptionId: number;
  companyId: number;
  mpPaymentId: string;
  mpPaymentType: string;
  mpPaymentMethodId: string;
  mpStatus: string;
  mpStatusDetail: string;
  transactionAmount: number;
  netReceivedAmount: number;
  currencyId: string;
  dateCreated?: string;
  dateApproved?: string;
  cardLastFourDigits?: string;
  createdAt: string;
}

// Billing Status (status de assinatura da empresa)
export interface BillingStatus {
  status: string; // pending, active, expired, cancelled, trial
  subscriptionStatus?: string; // pending, active, expired, cancelled, trial
  hasPaidSubscription: boolean;
  subscriptionExpiresAt?: string | null;
  expiresAt?: string | null;
  trialEndsAt?: string | null;
  lastPaymentAt?: string | null;
  billingCycleDays: number;
  planId?: number;
  planName?: string;
  isBlocked: boolean;
  isSuperadmin?: boolean;
  wasActive?: boolean; // true if the user had an active subscription before
}

export const billingApi = {
  // Get available plans (public)
  getPlans: () => api.get<SubscriptionPlan[]>('/settings/plans'),

  // Create checkout (PIX, boleto, cartão) - Checkout Pro
  createCheckout: (data: CreateCheckoutRequest) =>
    api.post<CheckoutResult>('/billing/checkout', data),

  // Create subscription (recurring - deprecated, use createCheckout)
  createSubscription: (data: CreateSubscriptionRequest) =>
    api.post<SubscriptionResult>('/billing/subscriptions', data),

  // Get billing status (company subscription status)
  getBillingStatus: () =>
    api.get<BillingStatus>('/billing/status'),

  // Get current subscription
  getCurrentSubscription: () =>
    api.get<MercadoPagoSubscription>('/billing/subscriptions/current'),

  // List all subscriptions
  listSubscriptions: () =>
    api.get<MercadoPagoSubscription[]>('/billing/subscriptions'),

  // Cancel subscription
  cancelSubscription: (id: number) =>
    api.post(`/billing/subscriptions/${id}/cancel`),

  // Pause subscription
  pauseSubscription: (id: number) =>
    api.post(`/billing/subscriptions/${id}/pause`),

  // Get subscription payments
  getPayments: (subscriptionId: number) =>
    api.get<MercadoPagoPayment[]>(`/billing/subscriptions/${subscriptionId}/payments`),

  // Validate coupon code
  validateCoupon: (data: { code: string; planId: number; amount: number }) =>
    api.post<{
      valid: boolean;
      code?: string;
      discountAmount?: number;
      finalPrice?: number;
      type?: string;
      discountValue?: number;
      error?: string;
    }>('/coupons/validate', data),

  // Apply coupon to activate plan (for free coupons)
  applyCouponToActivatePlan: (data: { couponCode: string; planId: number; originalPrice: number }) =>
    api.post<{
      success: boolean;
      message?: string;
      error?: string;
    }>('/coupons/apply-and-activate', data),
};

export interface CreateCompanyRequest {
  name: string;
  email: string;
  plan?: string;
  maxConnections?: number;
  adminName?: string;
  adminEmail?: string;
  adminPassword?: string;
}

export const adminApi = {
  // Dashboard
  getDashboardStats: () => api.get<DashboardStats>('/admin/dashboard'),

  // Companies
  getCompanies: () => api.get<Company[]>('/admin/companies'),
  getCompany: (id: number) => api.get<CompanyDetails>(`/admin/companies/${id}`),
  createCompany: (data: CreateCompanyRequest) => api.post<Company>('/admin/companies', data),
  updateCompany: (id: number, data: Partial<Company>) => api.put<Company>(`/admin/companies/${id}`, data),
  deleteCompany: (id: number) => api.delete(`/admin/companies/${id}`),
  toggleCompanyStatus: (id: number) => api.post<Company>(`/admin/companies/${id}/toggle-status`),

  // Company Users Management
  getCompanyUsers: (companyId: number) => api.get<User[]>(`/admin/companies/${companyId}/users`),
  createCompanyUser: (companyId: number, data: { name: string; email: string; password: string; role: string }) =>
    api.post<User>(`/admin/companies/${companyId}/users`, data),
  updateCompanyUser: (companyId: number, userId: number, data: Partial<User>) =>
    api.put<User>(`/admin/companies/${companyId}/users/${userId}`, data),
  deleteCompanyUser: (companyId: number, userId: number) =>
    api.delete(`/admin/companies/${companyId}/users/${userId}`),

  // Company Settings/Modules
  getCompanySettings: (companyId: number) => api.get<CompanySettings>(`/admin/companies/${companyId}/settings`),
  updateCompanySettings: (companyId: number, data: Partial<CompanySettings>) =>
    api.put<CompanySettings>(`/admin/companies/${companyId}/settings`, data),

  // Subscriptions
  getSubscriptions: () => api.get<AdminSubscription[]>('/admin/subscriptions'),
  changeSubscriptionPlan: (id: number, planId: number) =>
    api.put<AdminSubscription>(`/admin/subscriptions/${id}/change-plan`, { planId }),
  updateCompanySubscription: (companyId: number, data: { planId?: number; status?: string; maxConnections?: number }) =>
    api.put<AdminSubscription>(`/admin/companies/${companyId}/subscription`, data),

  // Invoices
  getInvoices: () => api.get<Invoice[]>('/admin/invoices'),
  createInvoice: (data: {
    companyId: number;
    subscriptionId?: number;
    items: InvoiceItem[];
    tax?: number;
    discount?: number;
    dueDate: string;
    notes?: string;
  }) => api.post<Invoice>('/admin/invoices', data),

  // Audit Logs
  getAuditLogs: (params?: {
    companyId?: number;
    userId?: number;
    action?: string;
    entity?: string;
  }) => api.get<AuditLog[]>('/admin/audit-logs', { params }),

  // System Settings
  getSystemSettings: () => api.get<AdminSystemSettings>('/admin/system-settings'),
  updateSystemSettings: (data: Partial<AdminSystemSettings>) =>
    api.put<AdminSystemSettings>('/admin/system-settings', data),
};

// ==================== PERSONAL KANBAN API ====================
export const personalKanbanApi = {
  // Lanes
  getLanes: (type: 'ticket' | 'deal' | 'task') =>
    api.get<PersonalKanbanLane[]>('/personal-kanban/lanes', { params: { type } }),
  createLane: (data: CreatePersonalKanbanLaneRequest) =>
    api.post<PersonalKanbanLane>('/personal-kanban/lanes', data),
  updateLane: (id: number, data: UpdatePersonalKanbanLaneRequest) =>
    api.put<PersonalKanbanLane>(`/personal-kanban/lanes/${id}`, data),
  deleteLane: (id: number) =>
    api.delete(`/personal-kanban/lanes/${id}`),
  reorderLanes: (type: 'ticket' | 'deal' | 'task', lanes: number[]) =>
    api.post('/personal-kanban/lanes/reorder', { type, lanes }),

  // Items
  getItems: (type: 'ticket' | 'deal' | 'task') =>
    api.get<PersonalKanbanItem[]>('/personal-kanban/items', { params: { type } }),
  addItem: (data: AddPersonalKanbanItemRequest) =>
    api.post<PersonalKanbanItem>('/personal-kanban/items', data),
  moveItem: (id: number, data: MovePersonalKanbanItemRequest) =>
    api.put<PersonalKanbanItem>(`/personal-kanban/items/${id}/move`, data),
  removeItem: (id: number) =>
    api.delete(`/personal-kanban/items/${id}`),
  reorderItems: (laneId: number, items: number[]) =>
    api.post('/personal-kanban/items/reorder', { laneId, items }),
};

// ==================== TASKS API ====================
// ==================== TASK QUEUES API ====================
export const taskQueuesApi = {
  getAll: () =>
    api.get<any[]>('/task-queues'),
  getById: (id: number) =>
    api.get<any>(`/task-queues/${id}`),
  create: (data: {
    name: string;
    color: string;
    isDefault: boolean;
    isPublic: boolean;
    assignedUserIds: number[];
  }) =>
    api.post<any>('/task-queues', data),
  update: (id: number, data: {
    name?: string;
    color?: string;
    isDefault?: boolean;
    isPublic?: boolean;
    assignedUserIds?: number[];
  }) =>
    api.put<any>(`/task-queues/${id}`, data),
  delete: (id: number) =>
    api.delete(`/task-queues/${id}`),
};

// ==================== TASKS API ====================
export const tasksApi = {
  getAll: (status?: string) =>
    api.get<Task[]>('/tasks', { params: status ? { status } : {} }),
  getById: (id: number) =>
    api.get<Task>(`/tasks/${id}`),
  getStats: () =>
    api.get<TaskStats>('/tasks/stats'),
  create: (data: CreateTaskRequest) =>
    api.post<Task>('/tasks', data),
  update: (id: number, data: UpdateTaskRequest) =>
    api.put<Task>(`/tasks/${id}`, data),
  delete: (id: number) =>
    api.delete(`/tasks/${id}`),
  move: (id: number, data: { laneId: number; order: number }) =>
    api.put<Task>(`/tasks/${id}/move`, data),
  complete: (id: number, data: CompleteTaskRequest) =>
    api.post<Task>(`/tasks/${id}/complete`, data),
  reorder: (laneId: number, tasks: number[]) =>
    api.post('/tasks/reorder', { laneId, tasks }),
  
  // Task Comments (Internal Chat)
  getComments: (taskId: number) =>
    api.get<TaskComment[]>(`/tasks/${taskId}/comments`),
  createComment: (taskId: number, message: string) =>
    api.post<TaskComment>(`/tasks/${taskId}/comments`, { message }),
  deleteComment: (taskId: number, commentId: number) =>
    api.delete(`/tasks/${taskId}/comments/${commentId}`),
};

// ==================== GROUP NOTIFICATIONS API ====================
export const groupNotificationsApi = {
  // List all groups with their notification settings
  getGroupsWithNotifications: () =>
    api.get<GroupWithNotifications[]>('/groups/with-notifications'),

  // Get notification settings for a specific group
  getGroupNotifications: (groupId: number) =>
    api.get<GroupNotificationSetting[]>(`/groups/${groupId}/notifications`),

  // Add a user to group notifications
  addUserToNotifications: (groupId: number, userId: number) =>
    api.post<GroupNotificationSetting>(`/groups/${groupId}/notifications`, { userId }),

  // Add multiple users to group notifications (bulk)
  setGroupNotifications: (groupId: number, userIds: number[]) =>
    api.post<GroupNotificationSetting[]>(`/groups/${groupId}/notifications/bulk`, { userIds }),

  // Toggle notification for a user
  toggleUserNotification: (groupId: number, userId: number, enabled: boolean) =>
    api.put<GroupNotificationSetting>(`/groups/${groupId}/notifications/${userId}`, { enabled }),

  // Remove a user from group notifications
  removeUserFromNotifications: (groupId: number, userId: number) =>
    api.delete(`/groups/${groupId}/notifications/${userId}`),
};

// ==================== BIRTHDAY API ====================
export const birthdayApi = {
  // Get birthday settings
  getSettings: () =>
    api.get<BirthdaySetting>('/birthdays/settings'),

  // Update birthday settings
  updateSettings: (data: UpdateBirthdaySettingsRequest) =>
    api.put<BirthdaySetting>('/birthdays/settings', data),

  // Get upcoming birthdays
  getUpcoming: (days?: number) =>
    api.get<BirthdayInfo[]>('/birthdays/upcoming', { params: { days } }),

  // Get today's birthdays
  getToday: () =>
    api.get<BirthdayInfo[]>('/birthdays/today'),

  // Send birthday message manually
  sendMessage: (data: SendBirthdayMessageRequest) =>
    api.post('/birthdays/send-message', data),

  // Get birthday logs
  getLogs: (year?: number, type?: 'user' | 'contact') =>
    api.get<BirthdayLog[]>('/birthdays/logs', { params: { year, type } }),
};

// ==================== SLA API ====================
export const slaApi = {
  // Get SLA statistics for the company
  getStats: (params?: { period?: string; startDate?: string; endDate?: string }) =>
    api.get<SLAStats>('/sla/stats', { params }),

  // Get tickets at risk of breaching SLA
  getAtRisk: () =>
    api.get<Array<{ ticket: Ticket; slaInfo: SLAInfo }>>('/sla/at-risk'),

  // Get tickets that have breached SLA
  getBreached: () =>
    api.get<Array<{ ticket: Ticket; slaInfo: SLAInfo }>>('/sla/breached'),

  // Get SLA info for a specific ticket
  getTicketSLA: (ticketId: number) =>
    api.get<SLAInfo>(`/sla/ticket/${ticketId}`),
};

// ==================== TICKET REOPEN REASONS API ====================
export const reopenReasonsApi = {
  // List all reopen reasons
  getAll: (active?: boolean) =>
    api.get<TicketReopenReason[]>('/reopen-reasons', { params: { active } }),

  // Get a specific reason
  getById: (id: number) =>
    api.get<TicketReopenReason>(`/reopen-reasons/${id}`),

  // Create a new reason
  create: (data: { name: string; description?: string; sortOrder?: number; requiresNote?: boolean }) =>
    api.post<TicketReopenReason>('/reopen-reasons', data),

  // Update a reason
  update: (id: number, data: Partial<{ name: string; description: string; sortOrder: number; requiresNote: boolean; isActive: boolean }>) =>
    api.put<TicketReopenReason>(`/reopen-reasons/${id}`, data),

  // Delete a reason
  delete: (id: number) =>
    api.delete(`/reopen-reasons/${id}`),

  // Seed default reasons
  seedDefaults: () =>
    api.post<TicketReopenReason[]>('/reopen-reasons/seed'),

  // Get stats
  getStats: () =>
    api.get<{ byReason: Array<{ reasonId: number; reasonName: string; count: number }>; totalReopens: number; uniqueReopenedTickets: number }>('/reopen-reasons/stats'),

  // Get ticket reopen logs
  getTicketLogs: (ticketId: number) =>
    api.get<TicketReopenLog[]>(`/tickets/${ticketId}/reopen-logs`),

  // Close ticket with reason (for reopened tickets)
  closeWithReason: (ticketId: number, data: CloseTicketWithReasonRequest) =>
    api.post<Ticket>(`/tickets/${ticketId}/close-with-reason`, data),
};

// ==================== INTERNAL CHAT API ====================
export const internalChatApi = {
  // Groups
  getGroups: () =>
    api.get<InternalChatGroup[]>('/internal-chat/groups'),

  getGroup: (id: number) =>
    api.get<InternalChatGroup>(`/internal-chat/groups/${id}`),

  createGroup: (data: { name: string; description?: string; memberIds?: number[] }) =>
    api.post<InternalChatGroup>('/internal-chat/groups', data),

  updateGroup: (id: number, data: { name?: string; description?: string; isActive?: boolean }) =>
    api.put<InternalChatGroup>(`/internal-chat/groups/${id}`, data),

  addMember: (groupId: number, data: { userId: number; role?: 'admin' | 'member' }) =>
    api.post<InternalChatGroupMember>(`/internal-chat/groups/${groupId}/members`, data),

  removeMember: (groupId: number, memberId: number) =>
    api.delete(`/internal-chat/groups/${groupId}/members/${memberId}`),

  syncQueueGroups: () =>
    api.post<{ success: boolean; createdGroups: number; groups: InternalChatGroup[] }>('/internal-chat/groups/sync-queues'),

  // Group Messages
  getGroupMessages: (groupId: number, params?: { page?: number; limit?: number }) =>
    api.get<{ messages: InternalChatMessage[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(`/internal-chat/groups/${groupId}/messages`, { params }),

  sendGroupMessage: (groupId: number, data: { message: string; mediaUrl?: string; mediaType?: string }) =>
    api.post<InternalChatMessage>(`/internal-chat/groups/${groupId}/messages`, data),

  // Direct Messages
  getConversations: () =>
    api.get<InternalChatConversation[]>('/internal-chat/conversations'),

  getDirectMessages: (userId: number, params?: { page?: number; limit?: number }) =>
    api.get<InternalChatMessage[]>(`/internal-chat/conversations/${userId}`, { params }),

  sendDirectMessage: (userId: number, data: { message: string; mediaUrl?: string; mediaType?: string }) =>
    api.post<InternalChatMessage>(`/internal-chat/conversations/${userId}`, data),

  // Media & Stats
  uploadMedia: (formData: FormData) =>
    api.post<{ mediaUrl: string; mimeType: string; mediaType: string }>('/internal-chat/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  getUnreadCount: () =>
    api.get<InternalChatUnreadCount>('/internal-chat/unread'),

  getOnlineUsers: () =>
    api.get<{ onlineUsers: number[] }>('/internal-chat/online-users'),

  // Mark as read
  markGroupAsRead: (groupId: number) =>
    api.post(`/internal-chat/groups/${groupId}/read`),

  markDirectAsRead: (userId: number) =>
    api.post(`/internal-chat/conversations/${userId}/read`),
};

// NPS (Net Promoter Score) API
export const npsApi = {
  getResponses: (params?: { page?: number; limit?: number; startDate?: string; endDate?: string }) =>
    api.get<{ responses: NPSResponse[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>('/nps', { params }),

  getStats: (params?: { startDate?: string; endDate?: string }) =>
    api.get<NPSStats>('/nps/stats', { params }),

  getTimeline: (params?: { days?: number; startDate?: string; endDate?: string }) =>
    api.get<NPSTimeline[]>('/nps/timeline', { params }),

  getByUser: (params?: { startDate?: string; endDate?: string }) =>
    api.get<NPSByUser[]>('/nps/by-user', { params }),

  getByQueue: (params?: { startDate?: string; endDate?: string }) =>
    api.get<NPSByQueue[]>('/nps/by-queue', { params }),

  getByTicket: (ticketId: number) =>
    api.get<NPSResponse>(`/tickets/${ticketId}/nps`),
};

// CRM Leads API
export const leadsApi = {
  getAll: (params?: { status?: string; source?: string; ownerId?: number; minValue?: number; maxValue?: number; search?: string; tags?: string; page?: number; limit?: number }) =>
    api.get<{ data: Lead[]; total: number; page: number; limit: number }>('/crm/leads', { params }),

  getById: (id: number) =>
    api.get<Lead>(`/crm/leads/${id}`),

  create: (data: Partial<Lead>) =>
    api.post<Lead>('/crm/leads', data),

  update: (id: number, data: Partial<Lead>) =>
    api.put<Lead>(`/crm/leads/${id}`, data),

  delete: (id: number) =>
    api.delete(`/crm/leads/${id}`),

  bulkUpdateStatus: (leadIds: number[], status: LeadStatus) =>
    api.post<{ message: string; updated: number }>('/crm/leads/bulk-update-status', { leadIds, status }),

  // Lead Interactions
  getInteractions: (leadId: number) =>
    api.get<LeadInteraction[]>(`/crm/leads/${leadId}/interactions`),

  createInteraction: (leadId: number, data: Partial<LeadInteraction>) =>
    api.post<LeadInteraction>(`/crm/leads/${leadId}/interactions`, data),

  // Lead Tasks
  getTasks: (leadId: number) =>
    api.get<LeadTask[]>(`/crm/leads/${leadId}/tasks`),

  createTask: (leadId: number, data: Partial<LeadTask>) =>
    api.post<LeadTask>(`/crm/leads/${leadId}/tasks`, data),

  updateTask: (leadId: number, taskId: number, data: Partial<LeadTask>) =>
    api.put<LeadTask>(`/crm/leads/${leadId}/tasks/${taskId}`, data),

  deleteTask: (leadId: number, taskId: number) =>
    api.delete(`/crm/leads/${leadId}/tasks/${taskId}`),

  // Lead Pipelines
  getPipelines: () =>
    api.get<LeadPipeline[]>('/crm/lead-pipelines'),

  createPipeline: (data: Partial<LeadPipeline>) =>
    api.post<LeadPipeline>('/crm/lead-pipelines', data),

  // Stats
  getStats: () =>
    api.get<{
      total: number;
      byStatus: Record<string, number>;
      bySource: Record<string, number>;
      totalValue: number;
      avgDealSize: number;
      conversionRate: number;
    }>('/crm/leads/stats'),
};

// ==================== BOARDS API (New Board System) ====================
export interface Board {
  id: number;
  companyId: number;
  ownerUserId: number;
  name: string;
  description: string;
  color: string;
  icon: string;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
  owner?: User;
  lanes?: BoardLane[];
  shares?: BoardShare[];
}

export interface BoardShare {
  id: number;
  boardId: number;
  shareType: 'user' | 'queue';
  userId?: number;
  queueId?: number;
  canEdit: boolean;
  createdAt: string;
  user?: User;
  queue?: Queue;
}

export interface BoardLane {
  id: number;
  boardId: number;
  companyId: number;
  name: string;
  color: string;
  order: number;
  createdAt: string;
  updatedAt: string;
  tasks?: BoardTask[];
}

export interface BoardTask {
  id: number;
  boardId: number;
  laneId: number;
  companyId: number;
  createdById: number;
  assignedUserId?: number;
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'in_progress' | 'completed';
  dueDate?: string;
  completedAt?: string;
  order: number;
  createdAt: string;
  updatedAt: string;
  lane?: BoardLane;
  createdBy?: User;
  assignedUser?: User;
  notes?: BoardTaskNote[];
}

export interface BoardNote {
  id: number;
  boardId: number;
  userId: number;
  message: string;
  createdAt: string;
  user?: User;
}

export interface BoardTaskNote {
  id: number;
  taskId: number;
  boardId: number;
  userId: number;
  message: string;
  createdAt: string;
  user?: User;
}

export interface BoardEvent {
  id: number;
  boardId: number;
  userId: number;
  eventType: string;
  laneId?: number;
  taskId?: number;
  noteId?: number;
  details: string;
  createdAt: string;
  user?: User;
}

export interface BoardShareOptions {
  users: User[];
  queues: Queue[];
}

export const boardsApi = {
  // Boards
  getAll: () =>
    api.get<Board[]>('/boards'),

  getShareOptionsForCreate: () =>
    api.get<BoardShareOptions>('/boards/share-options'),
  
  getById: (id: number) =>
    api.get<Board>(`/boards/${id}`),
  
  create: (data: { name: string; description?: string; color?: string; icon?: string }) =>
    api.post<Board>('/boards', data),
  
  update: (id: number, data: { name?: string; description?: string; color?: string; icon?: string; isArchived?: boolean }) =>
    api.put<Board>(`/boards/${id}`, data),
  
  delete: (id: number) =>
    api.delete(`/boards/${id}`),

  // Shares
  getShareOptions: (boardId: number) =>
    api.get<BoardShareOptions>(`/boards/${boardId}/share-options`),

  share: (boardId: number, data: { shareType: 'user' | 'queue'; userId?: number; queueId?: number; canEdit?: boolean }) =>
    api.post<BoardShare>(`/boards/${boardId}/shares`, data),
  
  unshare: (boardId: number, shareId: number) =>
    api.delete(`/boards/${boardId}/shares/${shareId}`),

  // Lanes
  createLane: (boardId: number, data: { name: string; color?: string; order?: number }) =>
    api.post<BoardLane>(`/boards/${boardId}/lanes`, data),
  
  updateLane: (boardId: number, laneId: number, data: { name?: string; color?: string; order?: number }) =>
    api.put<BoardLane>(`/boards/${boardId}/lanes/${laneId}`, data),
  
  deleteLane: (boardId: number, laneId: number) =>
    api.delete(`/boards/${boardId}/lanes/${laneId}`),
  
  reorderLanes: (boardId: number, lanes: number[]) =>
    api.post(`/boards/${boardId}/lanes/reorder`, { lanes }),

  // Tasks
  createTask: (boardId: number, data: { laneId: number; title: string; description?: string; priority?: string; assignedUserId?: number; dueDate?: string }) =>
    api.post<BoardTask>(`/boards/${boardId}/tasks`, data),
  
  updateTask: (boardId: number, taskId: number, data: { title?: string; description?: string; priority?: string; status?: string; assignedUserId?: number; dueDate?: string }) =>
    api.put<BoardTask>(`/boards/${boardId}/tasks/${taskId}`, data),
  
  moveTask: (boardId: number, taskId: number, data: { laneId: number; order: number }) =>
    api.put<BoardTask>(`/boards/${boardId}/tasks/${taskId}/move`, data),
  
  deleteTask: (boardId: number, taskId: number) =>
    api.delete(`/boards/${boardId}/tasks/${taskId}`),

  // Board Notes (Board Chat)
  getBoardNotes: (boardId: number) =>
    api.get<BoardNote[]>(`/boards/${boardId}/notes`),
  
  createBoardNote: (boardId: number, data: { message: string }) =>
    api.post<BoardNote>(`/boards/${boardId}/notes`, data),

  // Task Notes (Task Chat)
  getTaskNotes: (boardId: number, taskId: number) =>
    api.get<BoardTaskNote[]>(`/boards/${boardId}/tasks/${taskId}/notes`),
  
  createTaskNote: (boardId: number, taskId: number, data: { message: string }) =>
    api.post<BoardTaskNote>(`/boards/${boardId}/tasks/${taskId}/notes`, data),

  // Timeline
  getTimeline: (boardId: number, params?: { page?: number; limit?: number }) =>
    api.get<{ events: BoardEvent[]; total: number; page: number; limit: number }>(`/boards/${boardId}/timeline`, { params }),
};

// ==================== MEDIA LIBRARY API ====================
export const mediaLibraryApi = {
  getAll: (params?: { page?: number; limit?: number; mediaType?: string; status?: string; search?: string }) =>
    api.get<{ items: MediaLibraryItem[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>('/media-library', { params }),

  getById: (id: number) =>
    api.get<MediaLibraryItem>(`/media-library/${id}`),

  upload: (formData: FormData) =>
    api.post<MediaLibraryItem>('/media-library/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  delete: (id: number) =>
    api.delete(`/media-library/${id}`),

  send: (id: number, data: { phoneNumbers: string[]; caption?: string; connectionId?: number; isGroup?: boolean }) =>
    api.post<{ messageId?: string; sent?: number; failed?: number; results?: Record<string, string>; errors?: Record<string, string> }>(`/media-library/${id}/send`, data),

  revalidate: (id: number) =>
    api.post<{ message: string }>(`/media-library/${id}/revalidate`),
};

export default api;
